import type { SamenvattingGroep } from "./Samenvatting";

/**
 * De grafiek: gestapelde staven, staven naast elkaar, of een lijn — met een y-as die bij nul begint.
 *
 * <p>Liggende balkjes lezen prima voor een ranglijst, maar niet voor een tijdreeks: een periode
 * hoort op de x-as van links naar rechts te lopen, met de hoeveelheid omhoog.
 *
 * <p><b>In SVG en zonder grafiekbibliotheek.</b> Dit zijn rechthoeken, een paar lijnen en wat
 * tekst; een bibliotheek erbij halen kost meer dan het oplevert en legt een tweede manier van
 * opmaken over het scherm.
 *
 * <p><b>De y-as begint bij nul.</b> Niet onderhandelbaar: een as die bij het laagste staafje begint
 * maakt van een verschil van drie procent een verdubbeling in beeld. Dat is precies het soort
 * grafiek dat er overtuigend uitziet en niet klopt.
 *
 * <p><b>Gestapeld óf naast elkaar is een leeskeuze, geen opmaak.</b> Gestapeld lees je het totaal
 * per periode af en zie je de verhouding binnen die periode; naast elkaar vergelijk je de reeksen
 * onderling. Daarom staat die keuze bij de gebruiker en niet vast in de code.
 */

/**
 * De kleurindex van een reeks.
 *
 * <p>Normaal de plek in de rij, maar een restcategorie krijgt altijd de neutrale kleur. "Overig" en
 * "Onbekend" zijn geen reeks maar wat er overblijft; als vierde in de rij zouden ze de
 * waarschuwingskleur krijgen, en dan trekt het scherm de aandacht naar de groep waar het minst over
 * te zeggen is.
 */
export function kleurIndex(label: string, plek: number): number {
  const naam = (label ?? "").trim().toLowerCase();
  if (naam === "overig" || naam === "overige" || naam === "onbekend") {
    return 2;
  }
  return plek % 5;
}

export type GrafiekSoort = "STAAF" | "GESTAPELD" | "LIJN";

export interface StaafReeks {
  label: string;
  /** Per categorie een waarde; ontbreekt er een, dan is die null en wordt er niets getekend. */
  delen: { label: string; waarde: number | null }[];
}

/**
 * Een "mooie" bovengrens en een stapgrootte voor de as.
 *
 * <p>Zonder dit staat er 1, 2, 3… bij duizenden of komen er streepjes op 3,7 en 7,4. De as loopt
 * daarom naar het eerstvolgende veelvoud van 1, 2 of 5 maal een macht van tien.
 */
export function asSchaal(hoogste: number): { top: number; stap: number } {
  if (!(hoogste > 0)) return { top: 1, stap: 1 };
  const ruw = hoogste / 4;
  const macht = Math.pow(10, Math.floor(Math.log10(ruw)));
  const genormeerd = ruw / macht;
  const stap = (genormeerd <= 1 ? 1 : genormeerd <= 2 ? 2 : genormeerd <= 5 ? 5 : 10) * macht;
  return { top: Math.ceil(hoogste / stap) * stap, stap };
}

export function StaafGrafiek({
  reeksen,
  categorieen,
  titel,
  asTitel,
  soort = "GESTAPELD",
  toon,
}: {
  reeksen: StaafReeks[];
  /** De reekslabels voor de legenda; leeg bij één reeks. */
  categorieen: string[];
  /**
   * Waar de grafiek over gaat. Staat niet in beeld maar in het {@code aria-label}: de kaart
   * eromheen draagt de zichtbare titel al, en die twee keer opschrijven leest als twee dingen.
   */
  titel: string;
  /** Wat er op de y-as staat, bijvoorbeeld "Aantal dieren". */
  asTitel: string;
  /** Gestapeld, naast elkaar, of als lijn. */
  soort?: GrafiekSoort;
  toon: (w: number | null) => string;
}) {
  // Bij stapelen bepaalt het totaal per periode de hoogte, niet het hoogste losse deel. Zonder dat
  // onderscheid loopt de bovenste staaf de grafiek uit.
  const gestapeld = soort === "GESTAPELD" && categorieen.length > 1;
  const hoogste = gestapeld
    ? Math.max(...reeksen.map((r) => r.delen.reduce((s, d) => s + (d.waarde ?? 0), 0)), 0)
    : Math.max(...reeksen.flatMap((r) => r.delen.map((d) => d.waarde ?? 0)), 0);
  const { top, stap } = asSchaal(hoogste);

  // Vaste tekenmaten; het geheel schaalt mee via de viewBox.
  const H = 250;
  const marge = { boven: 14, onder: 30, links: 38, rechts: 8 };
  // De streefbreedte van het tekenvlak. Bij weinig groepen worden de staven breder tot ze het vlak
  // vullen; bij veel groepen houdt elke groep een leesbare minimumbreedte en wordt het vlak breder
  // dan het scherm — dan schuift het, in plaats van dat er tweehonderd haarlijntjes overblijven.
  const streef = 700;
  const aantal = Math.max(reeksen.length, 1);
  const perGroep = Math.max(44, streef / aantal);
  const B = marge.links + marge.rechts + perGroep * aantal;
  const vlakH = H - marge.boven - marge.onder;
  const y = (w: number) => marge.boven + vlakH - (w / top) * vlakH;

  const strepen: number[] = [];
  for (let w = 0; w <= top + 1e-9; w += stap) strepen.push(w);

  const delenPerGroep = Math.max(categorieen.length || 1, 1);
  // Binnen een groep delen de reeksen de ruimte; gestapeld staan ze op elkaar en is er één staaf.
  const breedPerDeel = gestapeld
    ? Math.min(64, perGroep * 0.56)
    : Math.max(6, Math.min(90, (perGroep * 0.7) / delenPerGroep));

  /** Het midden van een groep op de x-as. */
  const midden = (i: number) => marge.links + i * perGroep + perGroep / 2;

  return (
    <div className="fawkes-grafiek">
      <div className="fawkes-grafiek__body">
        <div className="fawkes-grafiek__vlak">
          <svg
            className="fawkes-grafiek__svg"
            viewBox={`0 0 ${B} ${H}`}
            role="img"
            aria-label={titel + " — " + asTitel}
            preserveAspectRatio="xMinYMid meet"
          >
            {/* De rasterlijnen met hun waarde ernaast. Zonder schaal is een staaf een plaatje. */}
            {strepen.map((w) => (
              <g key={w}>
                <line
                  className="fawkes-grafiek__raster"
                  x1={marge.links} x2={B - marge.rechts} y1={y(w)} y2={y(w)}
                />
                <text className="fawkes-grafiek__asgetal" x={marge.links - 8} y={y(w) + 4}
                  textAnchor="end">
                  {toon(w)}
                </text>
              </g>
            ))}

            {soort === "LIJN" ? (
              /* Eén lijn per categorie. Een ontbrekende waarde wordt overgeslagen en niet als nul
                 getekend: doortrekken door de bodem zou een meting suggereren die er niet is. */
              (categorieen.length > 0 ? categorieen : [""]).map((cat, j) => {
                const bijRij = (rij: StaafReeks) =>
                  cat === "" ? rij.delen[0] : rij.delen.find((d) => d.label === cat);
                const punten = reeksen
                  .map((rij, i) => {
                    const deel = bijRij(rij);
                    return deel?.waarde == null ? null : `${midden(i)},${y(deel.waarde)}`;
                  })
                  .filter((p): p is string => p !== null);
                return (
                  <g key={cat + j}>
                    <polyline
                      className={`fawkes-grafiek__lijn fawkes-grafiek__lijn--${kleurIndex(cat, j)}`}
                      points={punten.join(" ")}
                    />
                    {reeksen.map((rij, i) => {
                      const deel = bijRij(rij);
                      if (deel?.waarde == null) return null;
                      return (
                        <circle
                          key={i}
                          className={`fawkes-grafiek__punt fawkes-grafiek__staaf--${kleurIndex(deel.label, j)}`}
                          cx={midden(i)} cy={y(deel.waarde)} r={3.5}
                        >
                          <title>
                            {`${rij.label}${cat ? " · " + cat : ""}: ${toon(deel.waarde)}`}
                          </title>
                        </circle>
                      );
                    })}
                  </g>
                );
              })
            ) : (
              reeksen.map((rij, i) => {
                const delen = rij.delen.length > 0 ? rij.delen : [{ label: "", waarde: null }];
                const totaleBreedte = gestapeld ? breedPerDeel : breedPerDeel * delen.length;
                const start = marge.links + i * perGroep + (perGroep - totaleBreedte) / 2;
                // Bij stapelen loopt de onderkant op met wat er al onder ligt.
                let onder = 0;
                return (
                  <g key={rij.label + i}>
                    {delen.map((deel, j) => {
                      if (deel.waarde == null) return null;
                      const bovenkant = gestapeld ? y(onder + deel.waarde) : y(deel.waarde);
                      const hoogte = gestapeld
                        ? Math.max(0, y(onder) - y(onder + deel.waarde))
                        : Math.max(0, marge.boven + vlakH - y(deel.waarde));
                      if (gestapeld) onder += deel.waarde;
                      return (
                        <rect
                          key={deel.label + j}
                          className={`fawkes-grafiek__staaf fawkes-grafiek__staaf--${kleurIndex(deel.label, j)}`}
                          x={gestapeld ? start : start + j * breedPerDeel}
                          y={bovenkant}
                          width={Math.max(breedPerDeel - (gestapeld ? 0 : 2), 2)}
                          height={hoogte}
                        >
                          <title>
                            {`${rij.label}${deel.label ? " · " + deel.label : ""}: `
                              + toon(deel.waarde)}
                          </title>
                        </rect>
                      );
                    })}
                  </g>
                );
              })
            )}

            {/* De labels op de x-as staan los van de staven: bij een lijn zijn er geen staven om
                ze aan op te hangen. */}
            {reeksen.map((rij, i) => (
              <text key={rij.label + i} className="fawkes-grafiek__aslabel"
                x={midden(i)} y={H - marge.onder + 18} textAnchor="middle">
                {rij.label}
              </text>
            ))}

            {/* Geen titel langs de y-as: de kop van de kaart zegt al wat er geteld wordt, en een
                gedraaid woord van tien pixels kost meer breedte dan het oplevert. Wat er geteld
                wordt blijft wel in het aria-label van de grafiek staan. */}
            <line className="fawkes-grafiek__as" x1={marge.links} x2={B - marge.rechts}
              y1={marge.boven + vlakH} y2={marge.boven + vlakH} />
          </svg>
        </div>

        {/* De legenda onder de grafiek en gecentreerd: hij hoort bij de staven, niet bij de kop. */}
        {categorieen.length > 0 ? (
          <div className="fawkes-grafiek__legenda">
            {categorieen.map((c, i) => (
              <span className="fawkes-grafiek__legenda-item" key={c}>
                <i className={`fawkes-grafiek__stip fawkes-grafiek__staaf--${kleurIndex(c, i)}`} />
                {c}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** De groepen omzetten naar reeksen: per eerste label een staafgroep, per tweede label een staaf. */
export function reeksenVan(groepen: SamenvattingGroep[]): {
  reeksen: StaafReeks[];
  categorieen: string[];
} {
  const reeksen: StaafReeks[] = [];
  const categorieen: string[] = [];
  for (const g of groepen) {
    const eerste = g.labels[0] ?? "";
    const tweede = g.labels[1];
    let rij = reeksen.find((r) => r.label === eerste);
    if (!rij) {
      rij = { label: eerste, delen: [] };
      reeksen.push(rij);
    }
    rij.delen.push({ label: tweede ?? "", waarde: g.waarde });
    if (tweede !== undefined && !categorieen.includes(tweede)) categorieen.push(tweede);
  }
  return { reeksen, categorieen };
}
